/**
 * Renders the sun's shadow map a handful of times per level and then stops, for levels
 * where nothing that casts a shadow ever moves (design D10).
 *
 * A grounds level is a still life for shadow purposes even though it can hold a walking
 * companion: the hedges, the bank, the grass and the backdrop are fixed, and a robot dog
 * following the player does not cast a real shadow while it walks (design D7 of
 * `robot-dog-follows-player`) — it gets an unlit contact disc instead, so the caster set stays
 * as still as the geometry. Re-rendering the sun's 2048² map sixty times a second is therefore
 * sixty identical pictures, and on a tile-based mobile GPU each one is a full render target
 * resolved out to memory. Frozen, the map is drawn once and sampled forever. The house never
 * mounts this: its doors swing and its boxes get carried.
 *
 * **Why this cannot capture an empty or half-built map.** The freeze is not applied on
 * mount. `autoUpdate` is left alone for `SHADOW_SETTLE_FRAMES` frames, during which the map
 * renders normally, and only then is `needsUpdate` set for one last render before
 * `autoUpdate` goes off. Four frames rather than one because the scene is not finished
 * being *placed* on the first: drei's `Instances` composes its instance matrices in its own
 * `useFrame`, so the hedges are still stacked at the origin until that has run at least
 * once, and `Preload`'s compile happens in a layout effect on the same commit. Textures are
 * not a risk — the shadow pass writes depth and never samples a colour map — but geometry
 * and transforms are, and four frames is comfortably past both.
 *
 * The ordering within the freeze frame matters and is deliberate: `useFrame` callbacks all
 * run *before* `gl.render`, and this component mounts after the level, so its callback is
 * registered after the ones that place the instances. Setting `needsUpdate` here is
 * therefore a request the very next render honours, with everything already in place.
 *
 * **`sceneKey` is what keeps the freeze tied to a level rather than to the run.** Crossing
 * a portal swaps the whole world underneath this component without unmounting it — both
 * mazes are outdoors and both are built, so the condition it mounts under never changes.
 * Frozen shadows would then outlive the maze they were drawn for, and the arriving maze
 * would be lit by the departed one: everything the old map called shadow stayed black, and
 * the new hedges were simply not there. Handing `autoUpdate` back whenever the key changes
 * costs another four shadow renders per crossing — a fade's worth, once per portal.
 *
 * **A drag is one exception, a walking companion is the other.** Tables stand in the dead
 * ends and can be dragged, which moves a shadow-caster; the map therefore stays live for as
 * long as `draggedObjectId` is set and re-settles when the table is let go — a few seconds of
 * ordinary shadows per drag, against a table whose shadow would otherwise stay behind in the
 * cul-de-sac it came from. The robot dog is the other moving thing in a grounds level, but it
 * never casts (D7), so it does not need the map thawed for as long as the drag does — only a
 * re-settle at each end of the walk. `followerId` becoming non-null is the caster set changing
 * shape once (a real shadow is baked onto the table where the dog stood, and must be replaced
 * by nothing once the dog has walked off it); `followerId` going back to null, when the player
 * picks the dog up, is the same change in reverse (a fresh real shadow has to be baked wherever
 * the dog is set down). Both are one settle of `SHADOW_SETTLE_FRAMES`, not a sustained thaw —
 * the whole budget this file exists to protect stays spent on the sun alone, all game long.
 *
 * **A gap this file has always had, named so nobody mistakes it for the dog's doing.** A
 * carryable is not a drag and not a companion: pick a robot-dog leg off its table and walk
 * away with it, and the shadow it cast stays printed on the table behind you, because
 * `carriedObjectId` is not one of the settle effect's dependencies. That has been true since
 * this file was written, and `robot-dog-follows-player` deliberately did not fix it: the fix
 * is another dependency here, but the behaviour change is a thaw and a re-settle on every
 * pick-up and every drop, which on a level with five loose parts is a great many more shadow
 * renders than a walking dog costs — and that wants its own measurement on a handheld before
 * anyone spends it.
 *
 * In dev, an HMR edit unmounts this and remounts it. The cleanup hands `autoUpdate` back
 * and asks for one more render, and the fresh mount pays for another few frames of
 * shadows — so a hot edit can never leave a stale map on screen, at the cost of a handful
 * of shadow renders nobody will notice.
 */

import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';

import { useGameStore } from '../store/gameStore';

/** Frames of ordinary, auto-updating shadows before the map is frozen. */
export const SHADOW_SETTLE_FRAMES = 4;

type Props = {
  /**
   * Identifies the world currently below the canvas — the level id. Every change restarts
   * the settle, so the map is redrawn for the level the player is actually standing in.
   */
  sceneKey: string;
};

export function FrozenShadows({ sceneKey }: Props) {
  const gl = useThree((state) => state.gl);
  const dragging = useGameStore((state) => state.draggedObjectId !== null);
  const following = useGameStore((state) => state.followerId !== null);
  const settle = useRef(0);

  useEffect(() => {
    // A dragged table is a shadow-caster that moves for as long as the drag lasts, so the map
    // has to stay live for the whole of it — a settle would freeze it a few frames in and
    // leave the table's shadow lying on the grass where it was picked up. A settle of zero
    // is the frame loop's "stay thawed"; letting go re-runs this effect and restarts it,
    // which redraws the map once for where the table now stands.
    //
    // `following` flips exactly twice per run (the walk's start and its end) and never casts a
    // real shadow while true (D7), so it never needs the "stay thawed" treatment — only the
    // settle-and-refreeze this effect already does on any dependency change, which is what
    // clears the shadow the dog left on its table and bakes one where it is set down.
    settle.current = dragging ? 0 : SHADOW_SETTLE_FRAMES;
    gl.shadowMap.autoUpdate = true;

    return () => {
      gl.shadowMap.autoUpdate = true;
      gl.shadowMap.needsUpdate = true;
    };
  }, [gl, sceneKey, dragging, following]);

  useFrame(() => {
    if (settle.current === 0) return;
    settle.current -= 1;
    if (settle.current > 0) return;

    gl.shadowMap.autoUpdate = false;
    gl.shadowMap.needsUpdate = true;
  });

  return null;
}
